import React from 'react';
import { useSearchParams } from 'react-router-dom';
import './CoachingUnavailable.scss';
import { config } from '../../services/Constants/AppConstants';

enum CoachingUnavailableReason {
  SOLD_OUT = 'sold_out',
  REGISTRATION_CLOSED = 'registration_closed',
  NOT_FOUND = 'not_found',
}

interface ContentConfig {
  title: string;
  getMessage: (sessionName: string | null) => string;
  description: string;
}

const contentByReason: Record<CoachingUnavailableReason, ContentConfig> = {
  [CoachingUnavailableReason.SOLD_OUT]: {
    title: 'This Session is Full',
    getMessage: sessionName =>
      sessionName ? `${sessionName} has sold out!` : 'This coaching session has sold out!',
    description:
      'Thanks so much for your interest! This session is full, but we hope to see you in a future one.',
  },
  [CoachingUnavailableReason.REGISTRATION_CLOSED]: {
    title: 'Registration is Closed',
    getMessage: sessionName =>
      sessionName
        ? `Registration for ${sessionName} has closed.`
        : 'Registration for this coaching session has closed.',
    description:
      'The registration deadline for this session has passed. Keep an eye out for upcoming sessions!',
  },
  [CoachingUnavailableReason.NOT_FOUND]: {
    title: 'Session Not Found',
    getMessage: () => "We couldn't find the session you're looking for.",
    description:
      'The link may be expired or incorrect. Please check your email for the correct link, or explore our other programs below.',
  },
};

function CoachingUnavailable(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const sessionName = searchParams.get('session_name');
  const reasonParam = searchParams.get('reason') as CoachingUnavailableReason;
  const reason = Object.values(CoachingUnavailableReason).includes(reasonParam)
    ? reasonParam
    : CoachingUnavailableReason.SOLD_OUT;

  const content = contentByReason[reason];

  return (
    <div className="coaching-unavailable-container">
      <h2 className="title">{content.title}</h2>

      <p className="session-name">{content.getMessage(sessionName)}</p>

      <p className="description">{content.description}</p>

      <div className="secondary-links">
        <a
          href={`${config.WEBSITE_URL}/#programs`}
          rel="noopener noreferrer"
          className="secondary-link"
        >
          Explore Our Programs
        </a>
        <span className="divider" />
        <a
          href={`${config.WEBSITE_URL}/#contact`}
          rel="noopener noreferrer"
          className="secondary-link"
        >
          Contact Support
        </a>
      </div>
    </div>
  );
}

export default CoachingUnavailable;
